import { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { format, isValid, parseISO } from 'date-fns';
import {
    EditButton,
    ReferenceArrayField,
    ReferenceField,
    ReferenceManyField,
    ShowBase,
    useDataProvider,
    useDelete,
    useNotify,
    useRecordContext,
    useRedirect,
    useRefresh,
    useUpdate,
} from 'react-admin';

import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Target } from 'lucide-react';
import { DialogCloseButton } from '../misc/DialogCloseButton';
import { NotesIterator } from '../notes';
import { useConfigurationContext } from '../root/ConfigurationContext';
import { Quote, Sale } from '../types';
import { ContactList } from './ContactList';
import { findQuoteLabel } from './quote';
import { QuoteRatesDisplay } from './QuoteRatesDisplay';
import { GeneratePdfButton } from './GeneratePdfButton';

export const QuoteShow = ({ open, id }: { open: boolean; id?: string }) => {
    const redirect = useRedirect();
    const handleClose = () => {
        redirect('list', 'quotes');
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                },
            }}
        >
            <DialogContent sx={{ padding: 0, overflowX: 'auto' }}>
                {!!id ? (
                    <ShowBase id={id}>
                        <QuoteShowContent
                            handleClose={handleClose}
                            showViewPageButton
                        />
                    </ShowBase>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

const CLOSE_TOP_WITH_ARCHIVED = 14;
export const QuoteShowContent = ({
    handleClose,
    showViewPageButton = false,
    onEdit,
}: {
    handleClose: () => void;
    showViewPageButton?: boolean;
    onEdit?: () => void;
}) => {
    const { quoteStages } = useConfigurationContext();
    const record = useRecordContext<Quote>();
    const notify = useNotify();
    const redirect = useRedirect();
    if (!record) return null;

    return (
        <>
            <DialogCloseButton
                onClose={handleClose}
                top={record.archived_at ? CLOSE_TOP_WITH_ARCHIVED : 16}
                right={10}
                color={record.archived_at ? 'white' : undefined}
            />
            <Stack gap={1}>
                {record.archived_at ? <ArchivedTitle /> : null}
                <Box display="flex" p={2}>
                    <Box flex="1">
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            mb={4}
                        >
                            <Stack direction="row" alignItems="center" gap={2}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 40,
                                        height: 40,
                                        borderRadius: '8px',
                                        backgroundColor: '#ecfdf5',
                                    }}
                                >
                                    <Target size={24} color="#34d399" />
                                </Box>
                                <Typography variant="h5">
                                    {record.name}
                                </Typography>
                            </Stack>
                            <Stack
                                gap={1}
                                direction="row"
                                pr={record.archived_at ? 0 : 6}
                            >
                                {record.archived_at ? (
                                    <>
                                        <UnarchiveButton record={record} />
                                        <DeleteQuoteButton record={record} />
                                    </>
                                ) : (
                                    <>
                                        {onEdit ? (
                                            <Button
                                                size="small"
                                                startIcon={<EditNoteIcon />}
                                                onClick={onEdit}
                                            >
                                                Edit
                                            </Button>
                                        ) : (
                                            <EditButton scrollToTop={false} />
                                        )}
                                        <DeleteQuoteButton record={record} />
                                    </>
                                )}
                            </Stack>
                        </Stack>

                        {record.description && (
                            <Box mx={2} mb={2} sx={{ whiteSpace: 'pre-line' }}>
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    Description
                                </Typography>
                                <Typography variant="body2">
                                    {record.description}
                                </Typography>
                            </Box>
                        )}

                        <Box display="flex" m={2}>
                            <Box display="flex" mr={5} flexDirection="column">
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    Target Date
                                </Typography>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Typography variant="body2">
                                        {record.targetDate &&
                                        isValid(parseISO(record.targetDate))
                                            ? format(
                                                  parseISO(record.targetDate),
                                                  'PP'
                                              )
                                            : 'Not Set'}
                                    </Typography>
                                    {record.targetDate &&
                                    isValid(parseISO(record.targetDate)) &&
                                    parseISO(record.targetDate) < new Date() ? (
                                        <Chip
                                            label="Past"
                                            color="error"
                                            size="small"
                                        />
                                    ) : null}
                                </Stack>
                            </Box>

                            <Box display="flex" mr={5} flexDirection="column">
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    Stage
                                </Typography>
                                <Typography variant="body2">
                                    {findQuoteLabel(quoteStages, record.stage)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* --- HPT Fields Start --- */}
                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h6" sx={{ px: 2, pt: 1 }}>
                            Location & Crop
                        </Typography>
                        {record.quote_lines && record.quote_lines.length > 0 ? (
                            <Box px={2} py={1}>
                                {/* Header row */}
                                <Box display="flex" gap={5} mb={0.5}>
                                    <Typography
                                        color="textSecondary"
                                        variant="caption"
                                        sx={{ minWidth: 70 }}
                                    >
                                        State
                                    </Typography>
                                    <Typography
                                        color="textSecondary"
                                        variant="caption"
                                        sx={{ minWidth: 70 }}
                                    >
                                        County
                                    </Typography>
                                    <Typography
                                        color="textSecondary"
                                        variant="caption"
                                        sx={{ minWidth: 70 }}
                                    >
                                        Township
                                    </Typography>
                                    <Typography
                                        color="textSecondary"
                                        variant="caption"
                                        sx={{ minWidth: 50 }}
                                    >
                                        Range
                                    </Typography>
                                    <Typography
                                        color="textSecondary"
                                        variant="caption"
                                        sx={{ minWidth: 50 }}
                                    >
                                        Crop
                                    </Typography>
                                </Box>
                                {/* Data rows */}
                                {record.quote_lines.map((line, idx) => (
                                    <Box
                                        key={line.id || idx}
                                        display="flex"
                                        gap={5}
                                        py={0.5}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ minWidth: 70 }}
                                        >
                                            {line.state}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ minWidth: 70 }}
                                        >
                                            {line.county}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ minWidth: 70 }}
                                        >
                                            {line.township}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ minWidth: 50 }}
                                        >
                                            {line.range}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ minWidth: 50 }}
                                        >
                                            {line.crop}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box display="flex" flexWrap="wrap" p={2}>
                                <InfoItem
                                    label="State"
                                    value={record.state}
                                    mr={5}
                                />
                                <InfoItem
                                    label="County"
                                    value={record.county}
                                    mr={5}
                                />
                                <InfoItem
                                    label="Township"
                                    value={record.township}
                                    mr={5}
                                />
                                <InfoItem
                                    label="Range"
                                    value={record.range}
                                    mr={5}
                                />
                                <InfoItem label="Crop" value={record.crop} />
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h6" sx={{ px: 2, pt: 1 }}>
                            Policy Details
                        </Typography>
                        <Box display="flex" flexWrap="wrap" p={2}>
                            <Box display="flex" mr={5} flexDirection="column">
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    Amount
                                </Typography>
                                <Typography variant="body2">
                                    {record.amount != null
                                        ? `$${Number(record.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                        : '—'}
                                </Typography>
                            </Box>
                            <Box display="flex" mr={5} flexDirection="column">
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    Payment Method
                                </Typography>
                                <Typography variant="body2">
                                    {record.paymentMethod || '—'}
                                </Typography>
                            </Box>
                            <Box display="flex" mr={5} flexDirection="column">
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    Opp. Owner
                                </Typography>
                                <ReferenceField
                                    source="opportunityOwnerId"
                                    reference="sales"
                                    link={false}
                                >
                                    <OppOwnerName />
                                </ReferenceField>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Selections */}
                        <Typography variant="h6" sx={{ px: 2, pt: 1 }}>
                            Selections
                        </Typography>
                        <Box p={2}>
                            <Typography variant="body2" color="textSecondary">
                                Selected Carriers:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    mt: 0.5,
                                    minHeight: '24px',
                                }}
                            >
                                {record.selectedCarriers?.map(
                                    (carrier: string) => (
                                        <Chip
                                            key={carrier}
                                            label={
                                                carrier === 'proAg'
                                                    ? 'Pro Ag'
                                                    : carrier
                                            }
                                            size="small"
                                            sx={{ borderRadius: '16px' }}
                                        />
                                    )
                                )}
                                {(record.selectedCarriers?.length ?? 0) ===
                                    0 && (
                                    <Typography
                                        variant="caption"
                                        sx={{ fontStyle: 'italic' }}
                                    >
                                        None
                                    </Typography>
                                )}
                            </Box>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ mt: 1 }}
                            >
                                Selected Hail Products:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    mt: 0.5,
                                    minHeight: '24px',
                                }}
                            >
                                {record.selectedHailProducts?.map(
                                    (product: string) => (
                                        <Chip
                                            key={product}
                                            label={product}
                                            size="small"
                                            sx={{ borderRadius: '16px' }}
                                        />
                                    )
                                )}
                                {(record.selectedHailProducts?.length ?? 0) ===
                                    0 && (
                                    <Typography
                                        variant="caption"
                                        sx={{ fontStyle: 'italic' }}
                                    >
                                        None
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* --- HPT Fields End --- */}

                        {record.quote_lines &&
                            record.quote_lines.length > 0 && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{ px: 2, pt: 1 }}
                                    >
                                        <Typography variant="h6">
                                            Rates
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<EditNoteIcon />}
                                            onClick={() =>
                                                redirect(
                                                    `/hail-pricing-tool?quoteId=${record.id}`
                                                )
                                            }
                                        >
                                            Edit HPT
                                        </Button>
                                    </Box>
                                    <Box px={1} pb={2}>
                                        <QuoteRatesDisplay
                                            quoteLines={record.quote_lines}
                                            quoteId={record.id}
                                            selectedHailProducts={
                                                record.selectedHailProducts
                                            }
                                            paymentMethod={record.paymentMethod}
                                            state={record.state}
                                            selectedEndorsements={
                                                record.selectedEndorsements
                                            }
                                            endorsementRateLookup={
                                                record.endorsementRateLookup
                                            }
                                        />
                                    </Box>
                                </>
                            )}

                        {/* Show Edit in HPT for quotes with legacy HPT data but no quote_lines */}
                        {(!record.quote_lines ||
                            record.quote_lines.length === 0) &&
                            record.state && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{ px: 2, pt: 1 }}
                                    >
                                        <Typography variant="h6">
                                            Rates
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<EditNoteIcon />}
                                            onClick={() =>
                                                redirect(
                                                    `/hail-pricing-tool?quoteId=${record.id}`
                                                )
                                            }
                                        >
                                            Edit in HPT
                                        </Button>
                                    </Box>
                                </>
                            )}

                        {!!record.contact_ids?.length && (
                            <Box m={2}>
                                <Box
                                    display="flex"
                                    mr={5}
                                    flexDirection="column"
                                    minHeight={48}
                                >
                                    <Typography
                                        color="textSecondary"
                                        variant="caption"
                                    >
                                        Contacts
                                    </Typography>
                                    <ReferenceArrayField
                                        source="contact_ids"
                                        reference="contacts_summary"
                                    >
                                        <ContactList />
                                    </ReferenceArrayField>
                                </Box>
                            </Box>
                        )}

                        <Box m={2}>
                            <Divider />
                            <ReferenceManyField
                                target="quote_id"
                                reference="quoteNotes"
                                sort={{ field: 'date', order: 'DESC' }}
                            >
                                <NotesIterator reference="quotes" />
                            </ReferenceManyField>
                        </Box>

                        {/* Display notes if re-added */}
                        {record.notes && (
                            <Box m={2} sx={{ whiteSpace: 'pre-line' }}>
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    Notes
                                </Typography>
                                <Typography variant="body2">
                                    {record.notes}
                                </Typography>
                            </Box>
                        )}

                        {/* Generate PDF */}
                        <Divider sx={{ my: 1 }} />
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            px={2}
                            pb={1}
                        >
                            <GeneratePdfButton />
                        </Box>

                        {/* View Quote Page button — only in dialog */}
                        {showViewPageButton && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    px={2}
                                    pb={2}
                                >
                                    <Button
                                        size="small"
                                        startIcon={<OpenInNewIcon />}
                                        onClick={() =>
                                            redirect(
                                                `/quotes/${record.id}/page`
                                            )
                                        }
                                    >
                                        View Quote Page
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Stack>
        </>
    );
};

const ArchivedTitle = () => (
    <Box
        sx={{
            background: theme => theme.palette.warning.main,
            px: 3,
            py: 2,
        }}
    >
        <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
                color: theme => theme.palette.warning.contrastText,
            }}
        >
            Archived Quote
        </Typography>
    </Box>
);

const ArchiveButton = ({ record }: { record: Quote }) => {
    const [update] = useUpdate();
    const redirect = useRedirect();
    const notify = useNotify();
    const refresh = useRefresh();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleArchive = () => {
        update(
            'quotes',
            {
                id: record.id,
                data: { archived_at: new Date().toISOString() },
                previousData: record,
            },
            {
                onSuccess: () => {
                    setConfirmOpen(false);
                    redirect('list', 'quotes');
                    notify('Quote archived', { type: 'info', undoable: false });
                    refresh();
                },
                onError: () => {
                    setConfirmOpen(false);
                    notify('Error: quote not archived', { type: 'error' });
                },
            }
        );
    };

    return (
        <>
            <Button
                onClick={() => setConfirmOpen(true)}
                startIcon={<ArchiveIcon />}
                size="small"
            >
                Archive
            </Button>
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        pb: 1,
                    }}
                >
                    <ArchiveIcon sx={{ color: '#f59e0b' }} />
                    <Typography
                        variant="h6"
                        component="span"
                        sx={{ fontWeight: 600 }}
                    >
                        Archive Quote?
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                        Are you sure you want to archive{' '}
                        <strong>{record.name}</strong>? Archived quotes are
                        moved out of your active pipeline but can be restored
                        later.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{
                            textTransform: 'none',
                            color: '#64748b',
                            borderRadius: '8px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleArchive}
                        variant="contained"
                        startIcon={<ArchiveIcon />}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#f59e0b',
                            fontWeight: 600,
                            borderRadius: '8px',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#d97706',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Archive
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const UnarchiveButton = ({ record }: { record: Quote }) => {
    const dataProvider = useDataProvider();
    const redirect = useRedirect();
    const notify = useNotify();
    const refresh = useRefresh();

    const { mutate } = useMutation({
        mutationFn: () => dataProvider.unarchiveQuote(record),
        onSuccess: () => {
            redirect('list', 'quotes');
            notify('Quote unarchived', {
                type: 'info',
                undoable: false,
            });
            refresh();
        },
        onError: () => {
            notify('Error: quote not unarchived', { type: 'error' });
        },
    });

    const handleClick = () => {
        mutate();
    };

    return (
        <Button
            onClick={handleClick}
            startIcon={<UnarchiveIcon />}
            size="small"
        >
            Send back to the board
        </Button>
    );
};

const DeleteQuoteButton = ({ record }: { record: Quote }) => {
    const [deleteOne] = useDelete();
    const redirect = useRedirect();
    const notify = useNotify();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleDelete = () => {
        deleteOne(
            'quotes',
            { id: record.id, previousData: record },
            {
                onSuccess: () => {
                    setConfirmOpen(false);
                    redirect('list', 'quotes');
                    notify('Quote deleted', {
                        type: 'info',
                        undoable: false,
                    });
                },
                onError: () => {
                    setConfirmOpen(false);
                    notify('Error: quote not deleted', { type: 'error' });
                },
            }
        );
    };

    return (
        <>
            <Button
                onClick={() => setConfirmOpen(true)}
                startIcon={<DeleteIcon />}
                size="small"
                color="error"
            >
                Delete
            </Button>
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        pb: 1,
                    }}
                >
                    <DeleteIcon sx={{ color: '#ef4444' }} />
                    <Typography
                        variant="h6"
                        component="span"
                        sx={{ fontWeight: 600 }}
                    >
                        Delete Quote?
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                        Are you sure you want to permanently delete{' '}
                        <strong>{record.name}</strong>? This action cannot be
                        undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{
                            textTransform: 'none',
                            color: '#64748b',
                            borderRadius: '8px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#ef4444',
                            fontWeight: 600,
                            borderRadius: '8px',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#dc2626',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Helper component to display simple label/value pairs
const InfoItem = ({
    label,
    value,
    mr,
}: {
    label: string;
    value?: string | number | null;
    mr?: number;
}) =>
    value ? (
        <Box display="flex" mr={mr || 0} flexDirection="column">
            <Typography color="textSecondary" variant="caption">
                {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    ) : null;

// Helper component to display Opportunity Owner name from ReferenceField
const OppOwnerName = () => {
    const record = useRecordContext<Sale>();
    return record ? (
        <Typography variant="body2">{`${record.first_name} ${record.last_name}`}</Typography>
    ) : null;
};
